// CFG-IA.B1 (#1541) — wizard step: GitHub infrastructure (org + PAT).
// #1616 — adds an OAuth-connect entry point (primary), PAT stays as fallback.
// Extracted from Step1Infrastructure (storage + github were combined).
//
// #1541 shared-form extraction (Path B, locked): this step used to hand-roll
// its own org/token inputs, saved via POST /infrastructure-config with no
// live validation of the token — a syntactically-plausible but wrong/expired
// PAT could be saved silently, only discoverable later via a separate "Test
// Connection" click. It now renders GithubPatOrgForm, the same shared form
// GitHubConnectionCard's PAT-fallback uses, which saves the token via
// PATCH /credentials/github (live-validated against GitHub's API before
// persisting — a bad token is now rejected at save time, so "Test
// Connection" is gone; save IS the test) and the org via its own independent
// POST /infrastructure-config call, matching StorageProviderStep's /
// EstimationPreferenceStep's instant-save-per-action pattern.
//
// "Continue" is pure navigation here (not a save trigger) — GitHubInfraData
// is built from whatever GithubPatOrgForm's onSaved has last reported, fed
// both by its own initial-state fetch and by each explicit save. Continue is
// disabled until that initial-state fetch has resolved at least once, so a
// very fast click can't ship the (possibly-stale) savedData seed instead of
// the freshly-fetched server truth.
//
// #1616 — OAuth entry point. GithubPatOrgForm stays UNCONDITIONALLY mounted
// (never hidden behind a toggle) — it is the step's only status source (its
// mount fetch against /tenants/:id/infrastructure-config, widened by #1616
// to also reflect OAuth-set tokens, is what feeds `ready`/`current` below),
// so gating its visibility on that same status would create a chicken-and-
// egg race. Instead the OAuth CTA is an ADDITIONAL element shown above the
// always-present form, mirroring GitHubConnectionCard's "OAuth primary, PAT
// fallback" layout without duplicating GitHubConnectionCard's separate
// /byoi/github/status fetch (GithubPatOrgForm's own docstring is explicit
// that a host should never need a duplicate fetch of its own). The CTA
// disappears once `current.githubTokenSet` is true (via either path) and
// the form itself then renders its own "already connected" (masked-token)
// state — no separate connected-state UI needed here.
//
// The wizard is admin-only (OnboardingPage gates entry to admin roles), so
// GithubPatOrgForm's isAdmin prop stays hardcoded `true` as before.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../../lib/api';
import GithubPatOrgForm, { type GithubPatOrgState } from '../../settings/GithubPatOrgForm';

export interface GitHubInfraData {
  githubOrg:      string;
  githubTokenSet: boolean;
}

interface Props {
  tenantId:       string;
  onStepComplete: (data: GitHubInfraData) => void;
  savedData?:     GitHubInfraData;
}

export default function GitHubInfraStep({ tenantId, onStepComplete, savedData }: Props) {
  const [current, setCurrent] = useState<GitHubInfraData>({
    githubOrg:      savedData?.githubOrg ?? '',
    githubTokenSet: savedData?.githubTokenSet ?? false,
  });
  const [ready, setReady] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  // #1616 — the wizard's /byoi/github/callback return flow. GitHub sends the
  // browser back to /onboarding?github=connected|denied (origin=wizard); the
  // step surfaces that as a dismissible inline banner, mirroring
  // GitHubConnectionCard's flashConnected/flashDenied handling of the same
  // two values on the Settings surface.
  const [searchParams, setSearchParams] = useSearchParams();
  const flashConnected = searchParams.get('github') === 'connected';
  const flashDenied    = searchParams.get('github') === 'denied';

  useEffect(() => {
    if (!flashConnected && !flashDenied) return;
    const id = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('github');
      setSearchParams(next, { replace: true });
    }, 4000);
    return () => clearTimeout(id);
  }, [flashConnected, flashDenied, searchParams, setSearchParams]);

  const handleFormSaved = (data: GithubPatOrgState) => {
    setCurrent(data);
    setReady(true);
  };

  const handleConnectOAuth = async () => {
    setConnectError(null);
    setConnectingOAuth(true);
    try {
      const res = await apiFetch<{ url: string }>('/byoi/github/connect?origin=wizard');
      window.location.href = res.url;
    } catch (e) {
      setConnectError(e instanceof Error ? e.message : 'Failed to start GitHub connect');
      setConnectingOAuth(false);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        GitHub
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
        Connect your GitHub organisation for repository operations.
      </p>

      {flashConnected && (
        <div
          role="status"
          data-testid="github-oauth-flash-connected"
          style={{
            padding: '8px 12px', background: 'rgba(16,185,129,0.1)',
            border: '1px solid var(--success)', borderRadius: 6,
            fontSize: 13, color: 'var(--success)', marginBottom: 16,
          }}
        >
          GitHub connected.
        </div>
      )}
      {flashDenied && (
        <div
          role="alert"
          data-testid="github-oauth-flash-denied"
          style={{
            padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
            border: '1px solid var(--warning)', borderRadius: 6,
            fontSize: 13, color: 'var(--warning)', marginBottom: 16,
          }}
        >
          GitHub authorization was cancelled — your account was not connected.
        </div>
      )}

      {ready && !current.githubTokenSet && (
        <div style={{ marginBottom: 20 }}>
          <button
            type="button"
            onClick={() => void handleConnectOAuth()}
            disabled={connectingOAuth}
            data-testid="github-oauth-connect-btn"
            style={{
              fontFamily:   'var(--font-body)',
              fontSize:     13,
              fontWeight:   600,
              padding:      '9px 16px',
              background:   '#1f2328',
              color:        '#fff',
              border:       'none',
              borderRadius: 'var(--radius-sm)',
              cursor:       connectingOAuth ? 'not-allowed' : 'pointer',
              opacity:      connectingOAuth ? 0.6 : 1,
            }}
          >
            {connectingOAuth ? 'Redirecting…' : 'Connect with GitHub'}
          </button>
          {connectError && (
            <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 8 }}>
              {connectError}
            </p>
          )}
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>
            Or use a personal access token below (GitHub Enterprise Server, or OAuth unavailable):
          </p>
        </div>
      )}

      <GithubPatOrgForm
        tenantId={tenantId}
        isAdmin={true}
        initialOrg={savedData?.githubOrg}
        onSaved={handleFormSaved}
      />

      <button
        type="button"
        onClick={() => onStepComplete(current)}
        disabled={!ready}
        data-testid="github-continue-btn"
        style={{
          fontFamily:   'var(--font-body)',
          fontSize:     14,
          fontWeight:   600,
          padding:      '10px 24px',
          background:   'var(--primary)',
          color:        '#fff',
          border:       'none',
          borderRadius: 'var(--radius-sm)',
          cursor:       ready ? 'pointer' : 'not-allowed',
          opacity:      ready ? 1 : 0.6,
          marginTop:    16,
        }}
      >
        Continue
      </button>
    </div>
  );
}
